"use client";

import { Row, Col, Form, Spinner, Alert } from "react-bootstrap";
import { Link } from "react-router-dom";
import { useSignUpForm } from "../hooks/use-signup-form";
import { useEffect, useState } from "react";
import { Controller } from "react-hook-form";
import PhoneInput from "react-phone-number-input";
import ResendConfirmationEmail from "../ResendConfirmationEmail";
import GoogleSignInButton from "../GoogleSignInButton";
import { CONTRACT_PDF_URL } from "../../../config";
import "react-phone-number-input/style.css";

enum DocumentType {
  CC = "CC",
  CE = "CE",
  PASSPORT = "PASSPORT",
}
const MOBILE_QUERY = "(max-width: 767.98px)";

const useIsMobile = () => {
  const [isMobile, setIsMobile] = useState(
    () => window.matchMedia(MOBILE_QUERY).matches
  );
  useEffect(() => {
    const media = window.matchMedia(MOBILE_QUERY);
    const onChange = () => setIsMobile(media.matches);
    media.addEventListener("change", onChange);
    return () => media.removeEventListener("change", onChange);
  }, []);
  return isMobile;
};

const SignUpForm = () => {
  const [showPassword, setShowPassword] = useState(false);
  // En móvil primero se elige el método (Google o correo); en PC se ve todo.
  const isMobile = useIsMobile();
  const [method, setMethod] = useState<"choose" | "email">("choose");

  const {
    isSubmitting,
    submitError,
    submitSuccess,
    activationMessage,
    registeredEmail,
    countriesData,
    isLoadingCountries,
    countriesError,
    isReferralCodeDisabled,
    isLoadingReferralCode,
    referralUserName,
    referralError,
    errors,
    control,
    register,
    getValues,
    handleSubmit,
    onSubmit,
    setValue,
    handleReferralCodeChange,
    googleEmail,
    signUpWithGoogle,
    cancelGoogle,
  } = useSignUpForm();


  useEffect(() => {
    if (countriesData?.data?.countries) {
      const colombia = countriesData.data.countries.find(
        (c: any) => c.name?.toLowerCase() === "colombia"
      );

      if (colombia) {
        setValue("country_id", colombia.id);
      }
    }
  }, [countriesData, setValue]);


  return (
    <>
      <div className="auth-main-content m-auto m-1230 px-3">
        <Row className="align-items-center">
          <Col lg={5} md={5} className="d-none d-lg-block">
            <img
              src="/images/login_mth.jpg"
              className="rounded-3"
              alt="register"
              width={646}
              height={804}
            />
          </Col>

          <Col lg={6} md={6}>
            <div className="p-4 p-sm-5">
              <div className="d-inline-block mb-4">
                <img
                  src="/images/logo.svg"
                  className="rounded-3 for-dark-logo"
                  alt="login"
                  width={200}
                />
              </div>

              <h3 className="fs-28 mb-2">Regístrate en MultyHealth</h3>
              
              {submitError && (
                <Alert variant="danger" className="mb-4">
                  {submitError}
                </Alert>
              )}

              {googleEmail ? (
                <Alert variant="info" className="mb-4">
                  Te estás registrando con tu cuenta de Google ({googleEmail}).
                  Completa tus datos para continuar.{" "}
                  <button
                    type="button"
                    className="btn btn-link p-0 align-baseline"
                    onClick={() => {
                      cancelGoogle();
                      setMethod("choose");
                    }}
                  >
                    Usar otro método
                  </button>
                </Alert>
              ) : null}

              {isMobile && method === "choose" && !googleEmail ? (
                <div className="login-options mb-4">
                  <GoogleSignInButton onCredential={signUpWithGoogle} />
                  <button
                    type="button"
                    className="login-option-btn"
                    onClick={() => setMethod("email")}
                  >
                    <i className="ri-mail-line" aria-hidden="true"></i>
                    <span>Registrarse con correo</span>
                  </button>
                  <p className="mt-2 mb-0">
                    ¿Ya tienes una cuenta?{" "}
                    <Link to="/" className="text-decoration-underline text-primary">
                      Inicia Sesión
                    </Link>
                  </p>
                </div>
              ) : (
              <Form onSubmit={handleSubmit(onSubmit)}>
                {isMobile && !googleEmail && (
                  <button
                    type="button"
                    className="btn btn-link p-0 mb-3 text-decoration-none fw-medium"
                    onClick={() => setMethod("choose")}
                  >
                    ← Otras opciones de registro
                  </button>
                )}

                {/* Referral Code */}
                <Form.Group className="mb-3">
                  <label className="label text-secondary">Código de Referido</label>
                  <div className="position-relative">
                    <Form.Control
                      type="text"
                      className="h-45"
                      placeholder="Ingresa el código de referido"
                      disabled={isReferralCodeDisabled}
                      {...register("referralCode", {
                        required: "El código de referido es obligatorio",
                        onBlur: handleReferralCodeChange,
                      })}
                    />
                    {isLoadingReferralCode && (
                      <div
                        className="position-absolute"
                        style={{
                          right: "10px",
                          top: "50%",
                          transform: "translateY(-50%)",
                        }}
                      >
                        <Spinner animation="border" size="sm" />
                      </div>
                    )}
                  </div>

                  {referralUserName && (
                    <small className="text-success">
                      Referido por: {getValues("referralCode")} - {referralUserName}
                    </small>
                  )}

                  {referralError && !referralUserName && getValues("referralCode") && (
                    <small className="text-danger">Código de referido no válido</small>
                  )}

                  {errors.referralCode && (
                    <small className="text-danger">{errors.referralCode.message}</small>
                  )}
                </Form.Group>

                <Row>
                  <Col lg={6} md={6}>
                    <Form.Group className="mb-3">
                      <label className="label text-secondary">Nombres</label>
                      <Form.Control
                        type="text"
                        className="h-45"
                        placeholder="Ingresa tus nombres"
                        {...register("firstName", {
                          required: "Los nombres son obligatorios",
                          minLength: {
                            value: 3,
                            message: "Debe tener al menos 3 caracteres",
                          },
                        })}
                      />
                      {errors.firstName && (
                        <small className="text-danger">{errors.firstName.message}</small>
                      )}
                    </Form.Group>
                  </Col>

                  <Col lg={6} md={6}>
                    <Form.Group >
                      <label className="label text-secondary">Apellidos</label>
                      <Form.Control
                        type="text"
                        className="h-45"
                        placeholder="Ingresa tus apellidos"
                        {...register("lastName", {
                          required: "Los apellidos son obligatorios",
                          minLength: {
                            value: 3,
                            message: "Debe tener al menos 3 caracteres",
                          },
                        })}
                      />
                      {errors.lastName && (
                        <small className="text-danger">{errors.lastName.message}</small>
                      )}
                    </Form.Group>
                  </Col>
                </Row>

                <Row>
                  <Col lg={6} md={6}>
                    <Form.Group >
                      <Form.Label className="label text-secondary">Tipo de documento</Form.Label>
                      <Form.Select
                        {...register("documentType", {
                          required: "El tipo de documento es obligatorio",
                        })}
                        className="h-45"
                        style={{ height: '50px', lineHeight: '1.5' }}
                      >
                        <option value="">Seleccione un tipo</option>
                        <option value={DocumentType.CC}>Cédula de ciudadanía</option>
                        <option value={DocumentType.CE}>Cédula de extranjería</option>
                        <option value={DocumentType.PASSPORT}>Pasaporte</option>
                      </Form.Select>

                      <Form.Control.Feedback type="invalid" className="text-warning">
                        {errors.documentType?.message}
                      </Form.Control.Feedback>
                    </Form.Group>
                  </Col>
                  <Col lg={6} md={6}>
                    <Form.Group >
                      <label className="label text-secondary">Documento</label>
                      <Form.Control
                        {...register("document", {
                          required: "El documento es obligatorio",
                          minLength: {
                            value: 6,
                            message: "El documento debe tener al menos 6 dígitos",
                          },
                          maxLength: {
                            value: 12,
                            message: "El documento no puede exceder 12 dígitos",
                          },
                          pattern: {
                            value: /^[0-9]+$/,
                            message: "El documento solo puede contener números",
                          },
                        })}
                      />
                      {errors.document && (
                        <small className="text-danger">
                          {errors.document.message}
                        </small>
                      )}
                    </Form.Group>
                  </Col>
                </Row>

                <Row>
                  <Col lg={6} md={6}>
                    <Form.Group className="mb-3 mt-3">
                      <label className="label text-secondary">
                        Correo Electrónico
                      </label>
                      <Form.Control
                        type="email"
                        className="h-45"
                        placeholder="ejemplo@multyhealth.com"
                        readOnly={!!googleEmail}
                        {...register("email", {
                          required: "El correo electrónico es obligatorio",
                          pattern: {
                            value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                            message: "Dirección de correo inválida",
                          },
                        })}
                      />
                      {errors.email && (
                        <small className="text-danger">
                          {errors.email.message}
                        </small>
                      )}
                    </Form.Group>

                  </Col>
                  <Col lg={6} md={6}>
                    <Form.Group className="mb-3  mt-3">
                      <label className="label text-secondary">
                        País de Residencia
                      </label>
                      <Form.Select
                        className="form-control h-45"
                        // value={getValues("country_id")}
                        disabled={isLoadingCountries}
                        {...register("country_id", {
                          required: "Debes seleccionar un país",
                          onChange: (e) => setValue("country_id", e.target.value),
                        })}
                        value={
                          countriesData?.data?.countries?.find(
                            (c: any) => c.name?.toLowerCase() === "colombia"
                          )?.id || ""
                        }
                      >
                        <option value="">Selecciona tu país</option>
                        {countriesData?.data?.countries?.map(
                          (country: Record<string, any>) => (
                            <option key={country.id || ""} value={country.id || ""}>
                              {typeof country.name === "string" ? country.name : ""}
                            </option>
                          )
                        )}
                      </Form.Select>
                      {isLoadingCountries && (
                        <small className="text-muted">Cargando países...</small>
                      )}
                      {countriesError ? (
                        <small className="text-danger">
                          Error al cargar países. Intente nuevamente.
                        </small>
                      ) : null}
                      {errors.country_id && (
                        <small className="text-danger">
                          {errors.country_id.message}
                        </small>
                      )}
                    </Form.Group>
                  </Col>
                </Row>


                {/* Con Google no se pide contraseña: podrá definirla después. */}
                {!googleEmail && (
                  <Row>
                    <Col lg={6} md={6}>
                      <Form.Group className="mb-4">
                        <label className="label text-secondary">Contraseña</label>

                        <div style={{ position: "relative" }}>
                          <Form.Control
                            type={showPassword ? "text" : "password"}
                            className="h-45"
                            placeholder="Escribe tu contraseña"
                            {...register("password", {
                              required: "La contraseña es obligatoria",
                              minLength: {
                                value: 10,
                                message: "La contraseña debe tener al menos 10 caracteres",
                              },
                            })}
                            style={{ paddingRight: "40px" }}
                          />
                          <button
                            type="button"
                            onClick={() => setShowPassword(!showPassword)}
                            style={{
                              position: "absolute",
                              right: "10px",
                              top: "50%",
                              transform: "translateY(-50%)",
                              background: "transparent",
                              border: "none",
                              cursor: "pointer",
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                              padding: 0,
                            }}
                          >
                            {showPassword ? (
                              <span className="material-symbols-outlined">
                                visibility_off
                              </span>
                            ) : (
                              <span className="material-symbols-outlined">
                                visibility
                              </span>
                            )}
                          </button>
                        </div>

                        {errors.password && (
                          <small className="text-danger">
                            {errors.password.message}
                          </small>
                        )}
                      </Form.Group>
                    </Col>
                    <Col lg={6} md={6}>
                      <Form.Group className="mb-3">
                        <label className="label text-secondary">
                          Confirmar Contraseña
                        </label>
                        <Form.Control
                          type="password"
                          className="h-45"
                          placeholder="Confirma tu contraseña"
                          {...register("confirmPassword", {
                            required: "Debes confirmar tu contraseña",
                            minLength: {
                              value: 10,
                              message: "La contraseña debe tener al menos 10 caracteres",
                            },
                            validate: (
                              value: string,
                              formValues: Record<string, any>
                            ) =>
                              value === formValues.password ||
                              "Las contraseñas no coinciden",
                          })}
                        />
                        {errors.confirmPassword && (
                          <small className="text-danger">
                            {errors.confirmPassword.message}
                          </small>
                        )}
                      </Form.Group>
                    </Col>
                  </Row>
                )}

                <Row>
                  <Col lg={6} md={6}>
                    <Form.Group className="mb-3">
                      <label className="label text-secondary">Número de teléfono</label>
                      <Controller
                        name="cellphone"
                        control={control}
                        rules={{ required: "El teléfono es obligatorio" }}
                        render={({ field, fieldState }) => (
                          <>
                            <PhoneInput
                              {...field}
                              defaultCountry="CO"
                              value={field.value || ""}
                              onChange={field.onChange}
                              className={`form-control h-45 ${fieldState.invalid ? "is-invalid" : ""
                                }`}
                            />
                            {fieldState.error && (
                              <div className="invalid-feedback d-block">
                                {fieldState.error.message}
                              </div>
                            )}
                          </>
                        )}
                      />
                    </Form.Group>
                  </Col>

                  <Col lg={6} md={6}>
                    <Form.Group className="mb-3">
                      <label className="label text-secondary">Fecha de nacimiento</label>

                      <Row>
                        {/* Día */}
                        <Col xs={4}>
                          <Form.Control
                            type="number"
                            placeholder="DD"
                            {...register("birthDay", {
                              required: "Día requerido",
                              min: { value: 1, message: "Día inválido" },
                              max: { value: 31, message: "Día inválido" },
                            })}
                          />
                        </Col>

                        {/* Mes */}
                        <Col xs={4}>
                          <Form.Control
                            type="number"
                            placeholder="MM"
                            {...register("birthMonth", {
                              required: "Mes requerido",
                              min: { value: 1, message: "Mes inválido" },
                              max: { value: 12, message: "Mes inválido" },
                            })}
                          />
                        </Col>

                        {/* Año */}
                        <Col xs={4}>
                          <Form.Control
                            type="number"
                            placeholder="YYYY"
                            {...register("birthYear", {
                              required: "Año requerido",
                              validate: (value) => {
                                const year = Number(value);
                                const currentYear = new Date().getFullYear();

                                if (year < 1900 || year > currentYear) {
                                  return "Año inválido";
                                }

                                // validar +18
                                const age = currentYear - year;
                                if (age < 18) {
                                  return "Debes ser mayor de 18 años";
                                }

                                return true;
                              },
                            })}
                          />
                        </Col>
                      </Row>

                      {/* errores */}
                      {(errors.birthDay || errors.birthMonth || errors.birthYear) && (
                        <small className="text-danger d-block mt-1">
                          {errors.birthDay?.message ||
                            errors.birthMonth?.message ||
                            errors.birthYear?.message}
                        </small>
                      )}
                    </Form.Group>
                  </Col>
                </Row>
                <Form.Group className="mb-4">
                  <div className="form-check">
                    <input
                      type="checkbox"
                      className="form-check-input"
                      id="terms"
                      {...register("acceptTerms", {
                        required: "Debes aceptar los términos y condiciones",
                      })}
                    />
                    <label className="form-check-label" htmlFor="terms">
                      Acepto los{" "}
                      <a
                        href={CONTRACT_PDF_URL}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-decoration-underline text-primary"
                      >
                        Términos y Condiciones
                      </a>
                    </label>
                  </div>
                  {errors.acceptTerms && (
                    <small className="text-danger d-block">
                      {errors.acceptTerms.message}
                    </small>
                  )}
                </Form.Group>

                {submitSuccess && (
                  <Alert variant="success" className="mb-4">
                    {activationMessage ||
                      "¡Registro exitoso! Ya puedes iniciar sesión con tus credenciales."}
                    <div className="mt-2 pt-2 border-top">
                      <ResendConfirmationEmail email={registeredEmail} />
                    </div>
                  </Alert>
                )}

                <button
                  type="submit"
                  className="btn btn-primary w-100 mb-4 fw-semibold fs-16"
                  disabled={isSubmitting}
                >
                  {isSubmitting ? (
                    <>
                      <Spinner
                        as="span"
                        animation="border"
                        size="sm"
                        role="status"
                        aria-hidden="true"
                      />
                      <span className="ms-2">Procesando...</span>
                    </>
                  ) : (
                    "Registrarse"
                  )}
                </button>

                {/* En PC, Google va debajo del formulario (como en el login). */}
                {!isMobile && !googleEmail && (
                  <div className="mb-4">
                    <div className="d-flex align-items-center gap-2 mb-3 text-muted">
                      <hr className="flex-grow-1 m-0" />
                      <small>o</small>
                      <hr className="flex-grow-1 m-0" />
                    </div>
                    <GoogleSignInButton onCredential={signUpWithGoogle} />
                  </div>
                )}

                <div className="text-center">
                  <span className="fw-medium">
                    ¿Ya tienes una cuenta?{" "}
                    <Link
                      to="/"
                      className="text-decoration-underline text-primary"
                    >
                      Inicia Sesión
                    </Link>
                  </span>
                </div>
              </Form>
              )}
            </div>
          </Col>
        </Row>
      </div>
    </>
  );
};

export default SignUpForm;
